import { PROJECT_TYPE } from '@ps/survey-kernel';
import { Nullable } from '@ps/utils';

import type {
  GroupedParentStats,
  ProjectStats,
  Survey,
  TrackerParentStats,
} from './survey.model';

export interface SurveyClassification {
  readonly isStandard: boolean;
  readonly isMcqb: boolean;
  readonly isMcqbParent: boolean;
  readonly isMcqbChild: boolean;
  readonly isTracker: boolean;
  readonly isTrackerParent: boolean;
  readonly isTrackerWave: boolean;
  readonly isGrouped: boolean;
  readonly isGroupedParent: boolean;
  readonly isGroupedChild: boolean;
  readonly isProject: boolean;
  readonly isProjectParent: boolean;
  readonly isProjectChild: boolean;
  readonly groupedProject: Nullable<GroupedParentStats>;
  readonly trackerProject: Nullable<TrackerParentStats>;
}

interface McqbClassification {
  readonly isMcqb: boolean;
  readonly isMcqbParent: boolean;
  readonly isMcqbChild: boolean;
}

interface TrackerClassification {
  readonly isTracker: boolean;
  readonly isTrackerParent: boolean;
  readonly isTrackerWave: boolean;
  readonly trackerProject: Nullable<TrackerParentStats>;
}

interface GroupedClassification {
  readonly isGrouped: boolean;
  readonly isGroupedParent: boolean;
  readonly isGroupedChild: boolean;
  readonly groupedProject: Nullable<GroupedParentStats>;
}

function _isGroupedParent(
  project: ProjectStats,
): project is GroupedParentStats {
  return (
    project?.parent === true &&
    !('waveCount' in project) &&
    Array.isArray(project.surveys)
  );
}

function _isTrackerParent(
  project: ProjectStats,
): project is TrackerParentStats {
  return (
    project?.parent === true &&
    'waveCount' in project &&
    typeof project.waveCount === 'number'
  );
}

function _classifyMcqb(survey: Survey): McqbClassification {
  const isMcqb = survey.projectType === PROJECT_TYPE.MCQB;
  const isMcqbParent =
    isMcqb && survey.mc?.parent === true && survey.mc?.svp === true;
  return { isMcqb, isMcqbParent, isMcqbChild: isMcqb && !isMcqbParent };
}

function _classifyTracker(survey: Survey): TrackerClassification {
  const isTracker = survey.projectType === PROJECT_TYPE.TRACKER;
  const project = survey.project;
  const trackerProject =
    isTracker && _isTrackerParent(project) ? project : null;
  const isTrackerParent = trackerProject !== null;
  return {
    isTracker,
    trackerProject,
    isTrackerParent,
    isTrackerWave: isTracker && !isTrackerParent,
  };
}

function _classifyGrouped(survey: Survey): GroupedClassification {
  const isGrouped = survey.projectType === PROJECT_TYPE.GROUPED;
  const project = survey.project;
  const groupedProject =
    isGrouped && _isGroupedParent(project) ? project : null;
  const isGroupedParent = groupedProject !== null;
  return {
    isGrouped,
    groupedProject,
    isGroupedParent,
    isGroupedChild: isGrouped && !isGroupedParent,
  };
}

export function classifySurvey(survey: Survey): SurveyClassification {
  const mcqb = _classifyMcqb(survey);
  const tracker = _classifyTracker(survey);
  const grouped = _classifyGrouped(survey);

  const isProject = mcqb.isMcqb || tracker.isTracker || grouped.isGrouped;
  const isProjectParent =
    mcqb.isMcqbParent || tracker.isTrackerParent || grouped.isGroupedParent;
  const isProjectChild =
    mcqb.isMcqbChild || tracker.isTrackerWave || grouped.isGroupedChild;

  return {
    isStandard: !isProject,
    isMcqb: mcqb.isMcqb,
    isMcqbParent: mcqb.isMcqbParent,
    isMcqbChild: mcqb.isMcqbChild,
    isTracker: tracker.isTracker,
    isTrackerParent: tracker.isTrackerParent,
    isTrackerWave: tracker.isTrackerWave,
    isGrouped: grouped.isGrouped,
    isGroupedParent: grouped.isGroupedParent,
    isGroupedChild: grouped.isGroupedChild,
    isProject,
    isProjectParent,
    isProjectChild,
    groupedProject: grouped.groupedProject,
    trackerProject: tracker.trackerProject,
  };
}
